import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Calculator as CalcIcon, TrendingUp, Clock, IndianRupee, Zap, Car } from "lucide-react";
import { Slider } from "./ui/slider";
import { Button } from "./ui/button";

const ProfitCalculator = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  const [dailyCars, setDailyCars] = useState(15);
  const [avgKwhPerCar, setAvgKwhPerCar] = useState(25);
  const [sellingPrice] = useState(14); // Rs per kWh
  const [costPrice] = useState(6); // Rs per kWh (electricity + maintenance)

  // Calculations
  const profitPerKwh = sellingPrice - costPrice;
  const dailyKwh = dailyCars * avgKwhPerCar;
  const dailyProfit = dailyKwh * profitPerKwh;
  const monthlyProfit = dailyProfit * 30;
  const yearlyProfit = monthlyProfit * 12;
  
  // Investment assumptions
  const investment60kw = 1200000; // 12 Lakh
  const investment120kw = 1800000; // 18 Lakh
  const selectedInvestment = dailyCars > 20 ? investment120kw : investment60kw;
  
  const roiPercent = ((yearlyProfit / selectedInvestment) * 100).toFixed(0);
  const breakEvenMonths = Math.ceil(selectedInvestment / monthlyProfit);

  return (
    <section
      id="profit-calculator"
      className="py-24 md:py-32 bg-background relative overflow-hidden"
      data-testid="profit-calculator-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[200px] translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/5 rounded-full blur-[150px] -translate-x-1/2" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10" ref={containerRef}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">
            Franchise Opportunity
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground mt-4">
            CALCULATE YOUR
            <br />
            <span className="text-primary">PROFIT POTENTIAL</span>
          </h2>
          <p className="text-foreground-muted text-lg mt-6 max-w-2xl mx-auto">
            See how much you can earn by partnering with MILE PLUS. Adjust the parameters 
            to calculate your potential returns.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Calculator Inputs */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="glass-card"
          >
            <h3 className="font-heading text-xl text-foreground mb-8 flex items-center gap-3">
              <CalcIcon className="w-6 h-6 text-primary" />
              Adjust Parameters
            </h3>

            <div className="space-y-10">
              {/* Daily Cars */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="text-foreground flex items-center gap-2">
                    <Car className="w-5 h-5 text-primary" />
                    Daily Cars Charging
                  </label>
                  <span className="text-primary font-heading text-2xl">{dailyCars}</span>
                </div>
                <Slider
                  value={[dailyCars]}
                  onValueChange={(value) => setDailyCars(value[0])}
                  max={50}
                  min={5}
                  step={1}
                  className="w-full"
                  data-testid="profit-daily-cars-slider"
                />
                <div className="flex justify-between mt-2 text-xs text-foreground-muted">
                  <span>5 cars</span>
                  <span>50 cars</span>
                </div>
              </div>

              {/* Avg kWh per car */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="text-foreground flex items-center gap-2">
                    <Zap className="w-5 h-5 text-secondary" />
                    Avg. kWh per Vehicle
                  </label>
                  <span className="text-secondary font-heading text-2xl">{avgKwhPerCar}</span>
                </div>
                <Slider
                  value={[avgKwhPerCar]}
                  onValueChange={(value) => setAvgKwhPerCar(value[0])}
                  max={50}
                  min={10}
                  step={5}
                  className="w-full"
                  data-testid="profit-kwh-slider"
                />
                <div className="flex justify-between mt-2 text-xs text-foreground-muted">
                  <span>10 kWh</span>
                  <span>50 kWh</span>
                </div>
              </div>

              {/* Unit Economics */}
              <div className="pt-6 border-t border-white/10">
                <h4 className="text-foreground-muted text-sm mb-4 uppercase tracking-wider">
                  Unit Economics
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-background-subtle rounded-xl p-4">
                    <p className="text-foreground-muted text-xs">Selling Price</p>
                    <p className="text-foreground font-heading text-xl">Rs {sellingPrice}/kWh</p>
                  </div>
                  <div className="bg-background-subtle rounded-xl p-4">
                    <p className="text-foreground-muted text-xs">Operating Cost</p>
                    <p className="text-foreground font-heading text-xl">Rs {costPrice}/kWh</p>
                  </div>
                </div>
                <div className="mt-4 bg-primary/10 rounded-xl p-4 border border-primary/30">
                  <p className="text-primary text-xs">Profit Margin</p>
                  <p className="text-primary font-heading text-2xl">Rs {profitPerKwh}/kWh</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-6"
          >
            {/* Profit Cards */}
            <div className="grid grid-cols-2 gap-4">
              <motion.div 
                className="glass-card text-center"
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <IndianRupee className="w-6 h-6 text-primary" />
                </div>
                <p className="text-foreground-muted text-sm">Daily Profit</p>
                <p className="font-heading text-3xl text-primary mt-1">
                  Rs {dailyProfit.toLocaleString()}
                </p>
              </motion.div>
              <div className="glass-card text-center">
                <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="w-6 h-6 text-secondary" />
                </div>
                <p className="text-foreground-muted text-sm">Monthly Profit</p>
                <p className="font-heading text-3xl text-secondary mt-1">
                  Rs {(monthlyProfit / 1000).toFixed(0)}K
                </p>
              </div>
            </div>

            {/* Main Profit Card */}
            <div className="glass-card border-2 border-primary animate-pulse-glow">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
                  <IndianRupee className="w-8 h-8 text-black" />
                </div>
                <div>
                  <p className="text-foreground-muted text-sm">Yearly Profit Potential</p>
                  <p className="font-heading text-4xl text-primary">
                    Rs {(yearlyProfit / 100000).toFixed(1)} Lakh
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-6 border-t border-white/10">
                <div>
                  <p className="text-foreground-muted text-sm">ROI</p>
                  <p className="font-heading text-3xl text-foreground mt-1">
                    {roiPercent}%
                    <span className="text-sm text-foreground-muted font-normal"> /year</span>
                  </p>
                </div>
                <div>
                  <p className="text-foreground-muted text-sm">Break-even</p>
                  <p className="font-heading text-3xl text-foreground mt-1">
                    {breakEvenMonths}
                    <span className="text-sm text-foreground-muted font-normal"> months</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Investment Info */}
            <div className="glass-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-accent" />
                  <span className="text-foreground font-medium">
                    Recommended Package
                  </span>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  dailyCars > 20 
                    ? "bg-accent/20 text-accent" 
                    : "bg-primary/20 text-primary"
                }`}>
                  {dailyCars > 20 ? "120 kW" : "60 kW"}
                </span>
              </div>
              <p className="text-foreground-muted text-sm">
                Based on your projected traffic of <span className="text-primary font-medium">{dailyCars} cars/day</span>, 
                we recommend our {dailyCars > 20 ? "120 kW Ultra-Fast" : "60 kW Fast"} charger package 
                with an investment of <span className="text-primary font-medium">Rs {(selectedInvestment / 100000).toFixed(0)} Lakh</span>.
              </p>
            </div>

            {/* CTA */}
            <Button
              onClick={() => {
                const element = document.querySelector("#franchise");
                if (element) element.scrollIntoView({ behavior: "smooth" });
              }}
              className="w-full rounded-full bg-primary text-black font-bold hover:shadow-[0_0_20px_rgba(204,255,0,0.5)] transition-all h-14 text-lg"
              data-testid="profit-calc-cta"
            >
              Become a Franchise Partner
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProfitCalculator;
